import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useCart } from "../../storefront/CartContext";
import { ClipboardList, CheckCircle2, Package, Bike, PartyPopper, Loader2, XCircle, ShoppingBag, Search } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = [
  { key: "placed", label: "Order Placed", desc: "Aapka order mil gaya", Icon: ClipboardList },
  { key: "confirmed", label: "Order Confirmed", desc: "Humne call karke confirm kar liya", Icon: CheckCircle2 },
  { key: "packing", label: "Packing", desc: "Medicines pack ho rahi hain", Icon: Package },
  { key: "on_the_way", label: "On the Way", desc: "Order delivery par nikal gaya", Icon: Bike },
  { key: "delivered", label: "Delivered", desc: "Order pahunch gaya. Dhanyavaad!", Icon: PartyPopper },
];

function stageIndex(status) {
  if (status === "new") return 0;
  if (status === "called" || status === "accepted") return 1;
  if (status === "packing") return 2;
  if (status === "on_the_way") return 3;
  if (status === "delivered") return 4;
  return -1;
}

function stageTime(history, idx) {
  const map = idx === 0 ? ["new"] : idx === 1 ? ["called", "accepted"] : [STAGES[idx].key === "placed" ? "new" : STAGES[idx].key];
  const hit = (history || []).filter((h) => map.includes(h.status)).pop();
  if (!hit?.at) return "";
  return new Date(hit.at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
}

function ReorderButton({ order }) {
  const { add, clear } = useCart();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const reorder = async () => {
    setBusy(true);
    try {
      const { data: meds } = await axios.get(`${API}/public/medicines`);
      const found = [];
      const missing = [];
      for (const it of order.items || []) {
        const m = meds.find((x) => x.name === it.medicine_name);
        if (m && m.in_stock) found.push({ m, qty: it.quantity || 1 });
        else missing.push(it.medicine_name);
      }
      if (found.length === 0) {
        alert("In medicines mein se koi abhi stock mein nahi hai.");
        return;
      }
      clear();
      found.forEach(({ m, qty }) => add({ ...m, mrp: m.offer_price > 0 ? m.offer_price : m.mrp }, qty));
      if (missing.length) alert(`Ye abhi available nahi: ${missing.join(", ")}`);
      navigate("/store/cart");
    } catch {
      alert("Kuch gadbad ho gayi. Dobara try karein.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button onClick={reorder} disabled={busy} data-testid={`reorder-${order.order_no}`}
      className="inline-flex items-center gap-1.5 bg-[#064E3B] hover:bg-[#043B2C] disabled:opacity-60 text-white text-xs font-bold px-3 py-2 rounded-full transition-colors">
      <ShoppingBag className="w-3.5 h-3.5" /> {busy ? "Add ho raha…" : "Dobara Order Karein"}
    </button>
  );
}

function OrderCard({ o }) {
  const idx = stageIndex(o.status);
  const rejected = o.status === "rejected";
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={`track-order-${o.order_no}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="font-mono-num font-bold text-slate-900">{o.order_no}</div>
          <div className="text-xs text-slate-400">{new Date(o.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</div>
          {o.payment_method && <span className={`mt-1 inline-flex items-center text-[10px] font-bold px-2 py-0.5 rounded-full ${o.payment_method === "upi" ? "bg-indigo-100 text-indigo-700" : "bg-emerald-100 text-emerald-700"}`} data-testid={`track-pay-${o.order_no}`}>{o.payment_method === "upi" ? "UPI Payment" : "Cash on Delivery"}</span>}
        </div>
        {!rejected && <ReorderButton order={o} />}
        {rejected ? (
          <span className="inline-flex items-center gap-1.5 bg-red-100 text-red-700 text-xs font-bold px-3 py-1.5 rounded-full" data-testid={`track-status-${o.order_no}`}>
            <XCircle className="w-3.5 h-3.5" /> Cancelled
          </span>
        ) : idx === 4 ? (
          <span className="inline-flex items-center gap-1.5 bg-emerald-100 text-emerald-800 text-xs font-bold px-3 py-1.5 rounded-full" data-testid={`track-status-${o.order_no}`}>
            <PartyPopper className="w-3.5 h-3.5" /> Delivered
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 bg-amber-100 text-amber-800 text-xs font-bold px-3 py-1.5 rounded-full" data-testid={`track-status-${o.order_no}`}>
            <Bike className="w-3.5 h-3.5" /> {STAGES[idx].label}
          </span>
        )}
      </div>
      <div className="mt-2 text-xs text-slate-500">
        {o.items.map((it, i) => `${it.medicine_name} ×${it.quantity}`).join(", ") || "Prescription order"}
      </div>
      {rejected ? (
        <p className="mt-3 text-xs text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2.5">
          Ye order cancel/reject ho gaya tha. Sawal ho to store par call karein: +91 74288 11670
        </p>
      ) : (
        <div className="mt-4 space-y-0">
          {STAGES.map((s, i) => {
            const done = i <= idx;
            const active = i === idx;
            const time = done ? stageTime(o.history, i) : "";
            return (
              <div key={s.key} className="flex gap-3" data-testid={`track-stage-${o.order_no}-${s.key}`}>
                <div className="flex flex-col items-center">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 transition-colors ${done ? (active ? "bg-emerald-600 text-white" : "bg-emerald-100 text-emerald-700") : "bg-slate-100 text-slate-300"}`}>
                    <s.Icon className="w-4.5 h-4.5 w-5 h-5" />
                  </div>
                  {i < STAGES.length - 1 && <div className={`w-0.5 h-7 ${i < idx ? "bg-emerald-300" : "bg-slate-200"}`} />}
                </div>
                <div className="pb-6 pt-1.5 min-w-0">
                  <div className={`text-sm font-bold ${done ? "text-slate-900" : "text-slate-400"}`}>{s.label}</div>
                  <div className={`text-xs ${done ? "text-slate-500" : "text-slate-300"}`}>{time || s.desc}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function TrackOrder() {
  const [params] = useSearchParams();
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState(null);
  const [error, setError] = useState("");

  const run = async (digits) => {
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/public/track-order`, { params: { phone: digits } });
      setOrders(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Kuch gadbad ho gayi. Dobara try karein.");
      setOrders(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const p = (params.get("phone") || "").replace(/\D/g, "").slice(0, 10);
    if (p.length === 10) {
      setPhone(p);
      run(p);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const track = async (e) => {
    e?.preventDefault();
    const digits = phone.replace(/\D/g, "");
    if (digits.length !== 10) return setError("Sahi 10-digit mobile number daalein");
    run(digits);
  };

  return (
    <div className="max-w-xl mx-auto px-4 py-10" data-testid="track-order-page">
      <div className="text-center mb-8">
        <div className="w-14 h-14 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-3">
          <Package className="w-7 h-7 text-emerald-700" />
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Track Your Order</h1>
        <p className="text-slate-500 text-sm mt-2">Apna mobile number daalein — aapke saare orders ka live status dikhega. Login ki zaroorat nahi.</p>
      </div>

      <form onSubmit={track} className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
        <div className="flex gap-2">
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
            placeholder="10-digit mobile number"
            inputMode="numeric"
            data-testid="track-phone-input"
            className="flex-1 h-12 px-4 rounded-xl border border-slate-300 text-base font-mono-num focus:outline-none focus:ring-2 focus:ring-emerald-600"
          />
          <button type="submit" disabled={loading} data-testid="track-submit-btn"
            className="h-12 px-5 bg-[#064E3B] hover:bg-[#043B2C] disabled:opacity-60 text-white font-bold rounded-xl inline-flex items-center gap-2 transition-colors">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            <span className="hidden sm:inline">Track</span>
          </button>
        </div>
        {error && <div className="mt-2 text-xs text-red-600" data-testid="track-error">{error}</div>}
      </form>

      {orders && (
        <div className="mt-6 space-y-4" data-testid="track-results">
          {orders.length === 0 ? (
            <div className="text-center py-12 bg-white border border-slate-200 rounded-2xl" data-testid="track-empty">
              <ShoppingBag className="w-9 h-9 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">Is number par koi order nahi mila.</p>
              <Link to="/store/shop" className="mt-3 inline-flex items-center gap-2 text-emerald-700 font-semibold text-sm">Pehla order karein →</Link>
            </div>
          ) : (
            orders.map((o) => <OrderCard key={o.order_no} o={o} />)
          )}
        </div>
      )}
    </div>
  );
}
